import Image from "next/image";
import { SEAL_LABELS, LEGEND_LABELS, resolveLabeling, type NutritionData } from "@/lib/nom051";

/**
 * Sellos octagonales "EXCESO DE…" y leyendas precautorias de la NOM-051.
 * Se renderiza bajo el botón "Ir a la tienda" en la ficha del producto.
 */
export function NutritionSeals({
  nutrition,
  madeInMexico = false,
}: {
  nutrition: NutritionData | null | undefined;
  madeInMexico?: boolean;
}) {
  const { seals, legends } = resolveLabeling(nutrition);
  const hasLabeling = seals.length > 0 || legends.length > 0;
  if (!hasLabeling && !madeInMexico) return null;

  return (
    <div className="flex items-start gap-4 pt-1 min-h-[88px]">
      {hasLabeling && (
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">
            Etiquetado frontal · NOM-051
          </p>
          {seals.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {seals.map((key) => (
                <Octagon key={key} label={SEAL_LABELS[key]} />
              ))}
            </div>
          )}
          {legends.length > 0 && (
            <div className="mt-2 flex flex-col gap-1.5">
              {legends.map((key) => (
                <span
                  key={key}
                  className="inline-flex items-center self-start rounded-sm border-2 border-black bg-white px-2 py-1 text-[10px] font-extrabold uppercase leading-tight text-black"
                >
                  {LEGEND_LABELS[key]}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
      {madeInMexico && <MadeInMexicoBadge />}
    </div>
  );
}

/** Distintivo oficial "Hecho en México" de la Secretaría de Economía. */
function MadeInMexicoBadge() {
  return (
    <div className="shrink-0">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">Origen</p>
      <Image
        src="/badges/hecho-en-mexico.webp"
        alt="Hecho en México"
        title="Hecho en México"
        width={72}
        height={72}
        sizes="72px"
        quality={75}
        loading="lazy"
        className="h-[72px] w-[72px] rounded-md"
      />
    </div>
  );
}

/** Octágono negro con texto blanco, imitando el sello oficial. */
function Octagon({ label }: { label: string }) {
  const [first, ...rest] = label.split(" ");
  return (
    <div
      className="flex h-[72px] w-[72px] flex-col items-center justify-center bg-black px-1 text-center text-white"
      style={{
        clipPath:
          "polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)",
      }}
      role="img"
      aria-label={label}
    >
      <span className="text-[8px] font-bold uppercase leading-none">{first}</span>
      <span className="mt-0.5 text-[8px] font-extrabold uppercase leading-[1.05]">
        {rest.join(" ")}
      </span>
    </div>
  );
}
